import {useIntl} from 'react-intl';
import {useLocale} from './useLocale';

export const useTranslateMethod = () => {
  const intl = useIntl();
  const locale = useLocale();

  return <
    T extends ToolboxObject.TranslatableEntity & {
      subcategory?: ToolboxObject.Subcategory | null;
      original_method?: ToolboxObject.Method;
      steps: ToolboxObject.Steps | ToolboxObject.Info[];
      infos?: ToolboxObject.Infos | null;
      materials?: ToolboxObject.Material[] | null;
    }
  >(
    method: T
  ) => {
    const translations: Record<string, any> = {};
    (method?.translations || method.original_method?.translations || []).forEach((t) => {
      const {field_key, translation} = t;
      if (field_key in method && translation[locale]) {
        translations[field_key] = translation[locale];

        if (method.original_method) {
          translations.original_method = {
            ...method.original_method,
            ...(translations.original_method || {}),
            [field_key]: translation[locale],
          };
        }
      }
    });

    if (method.infos) {
      Object.entries(method.infos).forEach(([key, data]: [string, ToolboxObject.Info[]]) => {
        translations.infos = {
          ...translations.infos,
          [key]: data.map((info) => ({
            ...info,
            description: info.translation?.[locale] || info.description,
          })),
        };
      });
    }

    if (method.materials) {
      translations.materials = method.materials.map((material) => ({
        ...material,
        name: material.translation?.[locale] || material.name,
      }));
    }

    if (Array.isArray(method.steps)) {
      translations.steps = method.steps.map((step) => ({
        ...step,
        description: step.translation?.[locale] || step.description,
      }));
    } else {
      Object.entries(method.steps).forEach(([key, data]: [string, ToolboxObject.Info[]]) => {
        translations.steps = {
          ...translations.steps,
          [key]: data.map((info) => ({
            ...info,
            description: info.translation?.[locale] || info.description,
          })),
        };
      });
    }

    if (method.subcategory) {
      const subcategoryKey = method.subcategory.name.split(' ').join('_').toLowerCase();
      const categoryKey = method.subcategory.category.name.split(' ').join('_').toLowerCase();

      translations.subcategory = {
        ...method.subcategory,
        name: intl.formatMessage({id: `pages.library.subcategory.${subcategoryKey}`}),
        category: {
          ...method.subcategory.category,
          name: intl.formatMessage({id: `pages.library.category.${categoryKey}`}),
        },
      };
    }

    return {...method, ...translations} as T;
  };
};
